import type { DestinoPagamento, ItemPreVisualizacaoCsv, ResumoPagamentos } from '../types'

const TOL = 0.05
export const arred = (n: number) => Math.round(n * 100) / 100

/** Escolha da pessoa para um crédito de pagamento; `parte` é o texto do campo (só no "divide"). */
export interface EscolhaPagamento { destino: DestinoPagamento; parte: string }

/** Linha de pagamento que o servidor sugeriu destino e que será gravada como ajuste (não é a que já existe na conta). */
export const temSugestaoPagamento = (i: ItemPreVisualizacaoCsv) =>
  !!i.sugestaoPagamento && i.situacao === 'ajuste' && i.valor < 0 && !i.pagamentoFaturaAnterior

/** Linha já gravada na fatura errada: oferecer mover para a anterior. */
export const podeMoverParaAnterior = (i: ItemPreVisualizacaoCsv) =>
  !!i.sugestaoPagamento?.moverParaAnterior && !!i.transacaoExistenteId && !!i.sugestaoPagamento.faturaAnteriorId

export const temAnterior = (i: ItemPreVisualizacaoCsv) => !!i.sugestaoPagamento?.faturaAnteriorId

function parteInicial(i: ItemPreVisualizacaoCsv): string {
  const s = i.sugestaoPagamento!
  const v = Math.abs(i.valor)
  const p = s.parteAnterior > 0 && s.parteAnterior < v ? s.parteAnterior : Math.min(Math.max(s.saldoRestante, 0), v / 2)
  return p > 0 ? String(arred(p)) : ''
}

/** Escolha inicial = a sugestão do servidor (sem anterior, só dá para reduzir esta). */
export function escolhaInicial(i: ItemPreVisualizacaoCsv): EscolhaPagamento {
  const s = i.sugestaoPagamento!
  const destino = temAnterior(i) ? s.destino : 'reduzEsta'
  return { destino, parte: parteInicial(i) }
}

export const escolhaDe = (i: ItemPreVisualizacaoCsv, m: Record<number, EscolhaPagamento>) => m[i.indice] ?? escolhaInicial(i)

/** null = válido; senão a mensagem. Só o "divide" exige a parte entre 0 e o valor (exclusive). */
export function erroDaParte(i: ItemPreVisualizacaoCsv, e: EscolhaPagamento): string | null {
  if (e.destino !== 'divide') return null
  const p = Number(e.parte.replace(',', '.'))
  const v = Math.abs(i.valor)
  if (e.parte.trim() === '' || !Number.isFinite(p) || p <= 0 || p >= v) return `A parte da fatura anterior precisa ser maior que zero e menor que ${v.toFixed(2).replace('.', ',')}.`
  return null
}

/** Quanto do pagamento vai para a fatura anterior com a escolha atual. */
export function parteAnteriorDe(i: ItemPreVisualizacaoCsv, e: EscolhaPagamento, saldoSobrando: number): number {
  const v = Math.abs(i.valor)
  if (e.destino === 'quitaAnterior') return v
  if (e.destino === 'reduzEsta') return 0
  const p = Number(e.parte.replace(',', '.'))
  return Number.isFinite(p) && p > 0 && p < v ? arred(p) : Math.min(v, Math.max(saldoSobrando, 0))
}

/** Resumo calculado no cliente, igual ao do servidor (ver "Recalcular no cliente" da spec). `marcadas` = linhas que serão enviadas. */
export function calcularResumoPagamentos(marcadas: ItemPreVisualizacaoCsv[], m: Record<number, EscolhaPagamento>): ResumoPagamentos | null {
  const lista = marcadas.filter(temSugestaoPagamento)
  const base = lista[0]?.sugestaoPagamento ?? marcadas.find((i) => i.sugestaoPagamento)?.sugestaoPagamento
  if (!base) return null
  const saldo = arred(Math.max(base.saldoAnteriorInicial, 0))
  let total = 0
  for (const i of lista) total = arred(total + parteAnteriorDe(i, escolhaDe(i, m), saldo - total))
  const excede = total > saldo + TOL
  return {
    faturaAnteriorId: base.faturaAnteriorId, faturaAnteriorMes: base.faturaAnteriorMes, faturaAnteriorAno: base.faturaAnteriorAno,
    saldoAnteriorRestante: saldo, totalMarcadoParaAnterior: total, excedeSaldo: excede, excedente: excede ? arred(total - saldo) : 0,
  }
}

/** Campos a mandar na linha do confirmar. Sem sugestão (servidor antigo) = nada, comportamento de sempre. */
export function camposDoConfirmar(i: ItemPreVisualizacaoCsv, m: Record<number, EscolhaPagamento>): Pick<ItemPreVisualizacaoCsv, 'destinoPagamento' | 'valorParteAnterior'> {
  if (!temSugestaoPagamento(i)) return {}
  const e = escolhaDe(i, m)
  return e.destino === 'divide'
    ? { destinoPagamento: 'divide', valorParteAnterior: arred(Number(e.parte.replace(',', '.'))) }
    : { destinoPagamento: e.destino }
}
